import React from 'react';
import {
  Grid, FormControl, InputLabel, OutlinedInput, InputAdornment, Box, Typography,
  CircularProgress, Alert, Collapse, Checkbox, FormControlLabel,
} from '@mui/material';
import { ErrorWrapper } from '../../../resources/styling/appStyle';
import SchoolFormFields from '../../../components/common/SchoolFormFields';
import SectionCard from '../../../components/common/SectionCard';
import LogoUpload from '../../../components/common/LogoUpload';
import { sa, PillButton } from '../../../components/superAdminUi';

// Inputs, labels and controls tokenised to the Super Admin design system.
const fieldSx = {
  '& .MuiInputBase-root': { fontFamily: sa.font.family, fontSize: '14px', color: sa.color.text },
  '& .MuiInputLabel-root': { fontFamily: sa.font.family, fontSize: '14px', color: sa.color.textGray },
  '& .MuiInputLabel-root.Mui-focused': { color: sa.color.teal },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: sa.color.stroke },
  '& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': { borderColor: sa.color.teal },
  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: sa.color.teal },
};

const checkboxSx = {
  color: sa.color.stroke,
  '&.Mui-checked': { color: sa.color.teal },
};

const checkboxLabelSx = {
  m: 0,
  '& .MuiFormControlLabel-label': {
    fontFamily: sa.font.family,
    fontSize: 14,
    color: sa.color.text,
  },
};

// Step 1: URN-first. The GIAS quick-start leads (mirroring the group step's Type + GUID
// pairing), then the shared school form, logo and the test-account toggle. A group banner
// shows when the school is being added to a trust, and the inline duplicate warning pins.
export default function SchoolDetailsStep({
  formik,
  matId,
  groupName,
  customerTypes,
  giasLoading,
  giasSchoolData,
  showManual,
  duplicate,
  onSearch,
  onShowManual,
}) {
  const { values, touched, errors, setFieldValue } = formik;

  return (
    <Grid container spacing={1.5}>
      {matId && (
        <Grid item xs={12}>
          <Alert severity="info">Adding to {groupName || 'the group'}.</Alert>
        </Grid>
      )}

      <Grid item xs={12}>
        <SectionCard
          title="Have a URN? Autofill from GIAS"
          subtitle="Enter the school URN to load its details from GIAS, or enter the details manually."
        >
          <FormControl fullWidth sx={fieldSx}>
            <InputLabel htmlFor="school-urn">School URN</InputLabel>
            <OutlinedInput
              id="school-urn"
              label="School URN"
              value={values.Urn || ''}
              onChange={(e) => setFieldValue('Urn', e.target.value)}
              endAdornment={
                <InputAdornment position="end">
                  {giasLoading ? (
                    <CircularProgress size={20} sx={{ color: sa.color.teal }} />
                  ) : (
                    <PillButton type="button" onClick={onSearch}>Search</PillButton>
                  )}
                </InputAdornment>
              }
            />
            <ErrorWrapper>{touched.Urn && errors.Urn}</ErrorWrapper>
          </FormControl>

          {!showManual && (
            <Box sx={{ mt: 1.5 }}>
              <PillButton type="button" $variant="outline" onClick={onShowManual}>
                Enter details manually
              </PillButton>
            </Box>
          )}
        </SectionCard>
      </Grid>

      {giasSchoolData && (
        <Grid item xs={12}>
          <Box sx={{ p: 2, border: `1px solid ${sa.color.teal}`, borderRadius: sa.radius.lg, bgcolor: '#eef8fc' }}>
            <Typography variant="subtitle2" mb={1} sx={{ fontFamily: sa.font.family, color: sa.color.teal, fontWeight: sa.font.bold }}>
              School Details Loaded
            </Typography>
            <Typography variant="body2" color="text.secondary"><strong>Establishment Name:</strong> {giasSchoolData.EstablishmentName}</Typography>
            <Typography variant="body2" color="text.secondary"><strong>URN:</strong> {giasSchoolData.URN}</Typography>
            <Typography variant="body2" color="text.secondary"><strong>Address:</strong> {giasSchoolData.Street}, {giasSchoolData.Locality}, {giasSchoolData.Town}, {giasSchoolData.County_name}</Typography>
            <Typography variant="body2" color="text.secondary"><strong>Postcode:</strong> {giasSchoolData.Postcode || 'N/A'}</Typography>
            <Typography variant="body2" color="text.secondary"><strong>Number of Pupils:</strong> {giasSchoolData.NumberOfPupils || 'N/A'}</Typography>
            {giasSchoolData.HeadFirstName && (
              <Typography variant="body2" color="text.secondary">
                <strong>Head Teacher:</strong> {giasSchoolData.HeadFirstName} {giasSchoolData.HeadLastName} {giasSchoolData.HeadPreferredJobTitle ? `(${giasSchoolData.HeadPreferredJobTitle})` : ''}
              </Typography>
            )}
            {giasSchoolData.TelephoneNum && (
              <Typography variant="body2" color="text.secondary"><strong>Telephone:</strong> {giasSchoolData.TelephoneNum}</Typography>
            )}
          </Box>
        </Grid>
      )}

      {duplicate && duplicate.isDuplicate && (
        <Grid item xs={12}>
          <Alert severity="warning">
            A school with this {duplicate.matchedBy || 'name/URN'} already exists
            {duplicate.existingName ? `: ${duplicate.existingName}` : ''}.
          </Alert>
        </Grid>
      )}

      <Grid item xs={12}>
        <Collapse in={showManual} timeout="auto" unmountOnExit>
          <SchoolFormFields
            values={values}
            onFieldChange={setFieldValue}
            errors={{ Name: touched.Name && errors.Name }}
            variant="full"
            customerTypeOptions={customerTypes}
          />
        </Collapse>
      </Grid>

      <Grid item xs={12}>
        <LogoUpload
          label="School logo"
          helperText="PNG or JPEG. Shown across the school's dashboards."
          fileName={values.FileName}
          base64={values.File}
          onFile={({ base64, fileName }) => {
            setFieldValue('File', base64);
            setFieldValue('FileName', fileName);
          }}
        />
      </Grid>

      <Grid item xs={12}>
        <FormControlLabel
          control={<Checkbox checked={values.isTestAccount || false} onChange={(e) => setFieldValue('isTestAccount', e.target.checked)} sx={checkboxSx} />}
          label="Test Account"
          sx={checkboxLabelSx}
        />
      </Grid>
    </Grid>
  );
}
